/* ============================================================
   ELDR Cart — Upsell-Leiste & Rabattcode im Drawer.
   Alles ohne Seiten-Reload: Cart-AJAX-API mit sections=cart-drawer,
   danach rendert das Theme den Drawer über 'update:cart:drawer' neu.
   Die Custom Elements verbinden sich nach jedem Neu-Rendern selbst.
   ============================================================ */
(() => {
  if (window.EldrCart) return;

  const SECTION_ID = 'cart-drawer';
  const state = { upsellScroll: null, discountFlash: null };
  window.EldrCart = state;

  const route = (key, fallback) => (window.routes && window.routes[key]) || fallback;
  const cartJsUrl = () => `${route('cart_url', '/cart')}.js`;
  const cartAddUrl = () => `${route('cart_add_url', '/cart/add')}.js`;
  const cartUpdateUrl = () => `${route('cart_update_url', '/cart/update')}.js`;
  const sameCode = (a, b) => String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

  async function request(url, payload) {
    const options = payload
      ? {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        }
      : { headers: { Accept: 'application/json' } };

    const response = await fetch(url, options);
    let data = null;
    try {
      data = await response.json();
    } catch (e) {
      /* leere Antwort */
    }
    if (!response.ok) {
      const error = new Error((data && (data.description || data.message)) || response.statusText);
      error.status = response.status;
      throw error;
    }
    return data;
  }

  async function refreshCartCount() {
    if (!document.querySelector('[data-cart-count-indicator]') || !window.wetheme?.updateCartCount) return;
    try {
      const response = await fetch(`${route('cart_url', '/cart')}?view=compare`);
      window.wetheme.updateCartCount(await response.json());
    } catch (e) {
      /* Zähler ist Kosmetik */
    }
  }

  function renderDrawer(response) {
    const drawer = document.querySelector('cart-drawer');
    const scroller = drawer?.querySelector('[data-cart-scroll-element]');
    const scrollTop = scroller ? scroller.scrollTop : 0;
    const totalBefore = drawer?.querySelector('[data-eldr-total]')?.textContent.trim();

    if (!window.eventBus) {
      window.location.reload();
      return;
    }

    if (response?.sections?.[SECTION_ID]) {
      window.eventBus.emit('update:cart:drawer', response);
    } else {
      window.eventBus.emit('render:cart:drawer');
    }

    if (scroller) scroller.scrollTop = scrollTop;
    /* Nicht verbraucht (Leiste nach dem Rendern weg) -> nicht später falsch anwenden */
    state.upsellScroll = null;

    const totalNow = drawer?.querySelector('[data-eldr-total]');
    if (totalNow && totalBefore && totalNow.textContent.trim() !== totalBefore) {
      totalNow.classList.add('is-updated');
    }

    refreshCartCount();
  }

  /* ---------------- Upsell ---------------- */
  class EldrCartUpsell extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.updateNav = this.updateNav.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('[data-eldr-upsell-track]');
      this.navWrap = this.querySelector('[data-eldr-upsell-nav-wrap]');
      this.prev = this.querySelector('[data-eldr-upsell-nav="prev"]');
      this.next = this.querySelector('[data-eldr-upsell-nav="next"]');
      this.error = this.querySelector('[data-eldr-upsell-error]');

      this.addEventListener('click', this.onClick);
      this.track?.addEventListener('scroll', this.updateNav, { passive: true });
      window.addEventListener('resize', this.updateNav);

      if (this.track && state.upsellScroll !== null) {
        this.track.scrollLeft = state.upsellScroll;
        state.upsellScroll = null;
      }
      requestAnimationFrame(this.updateNav);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.track?.removeEventListener('scroll', this.updateNav);
      window.removeEventListener('resize', this.updateNav);
    }

    onClick(event) {
      const addButton = event.target.closest('[data-eldr-upsell-add]');
      if (addButton) {
        this.add(addButton);
        return;
      }
      const navButton = event.target.closest('[data-eldr-upsell-nav]');
      if (navButton && this.track) {
        this.goTo(navButton.dataset.eldrUpsellNav === 'next' ? 1 : -1);
      }
    }

    /* Nächste/vorige Karte exakt mittig einrasten (erste/letzte bleiben bündig) */
    goTo(direction) {
      const cards = [...this.track.querySelectorAll('.eldr-upsell__card')];
      if (!cards.length) return;
      const trackBox = this.track.getBoundingClientRect();
      const view = this.track.clientWidth;
      const centers = cards.map((card) => {
        const box = card.getBoundingClientRect();
        return box.left - trackBox.left + this.track.scrollLeft + box.width / 2;
      });
      /* Läuft noch eine Animation, vom letzten Ziel aus weiterzählen —
         sonst würde ein schneller Doppelklick dieselbe Karte erneut ansteuern. */
      let index = this.pendingIndex;
      if (index == null) {
        const current = this.track.scrollLeft + view / 2;
        index = 0;
        centers.forEach((center, i) => {
          if (Math.abs(center - current) < Math.abs(centers[index] - current)) index = i;
        });
      }
      const target = Math.min(cards.length - 1, Math.max(0, index + direction));
      const max = this.track.scrollWidth - view;
      const left = Math.min(max, Math.max(0, centers[target] - view / 2));

      this.pendingIndex = target;
      clearTimeout(this.pendingTimer);
      const release = () => {
        this.pendingIndex = null;
        clearTimeout(this.pendingTimer);
      };
      this.track.addEventListener('scrollend', release, { once: true });
      this.pendingTimer = setTimeout(release, 800);

      this.track.scrollTo({ left, behavior: 'smooth' });
    }

    updateNav() {
      if (!this.track || !this.navWrap) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.navWrap.hidden = max <= 2;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max - 2;
    }

    setError(message) {
      if (!this.error) return;
      this.error.textContent = message || '';
      this.error.hidden = !message;
    }

    async add(button) {
      if (button.getAttribute('aria-busy') === 'true') return;
      const id = Number(button.dataset.variantId);
      if (!id) return;

      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
      this.setError('');

      try {
        const data = await request(cartAddUrl(), {
          items: [{ id, quantity: 1 }],
          sections: SECTION_ID,
        });
        if (this.track) state.upsellScroll = this.track.scrollLeft;
        renderDrawer(data);
      } catch (error) {
        button.removeAttribute('aria-busy');
        button.disabled = false;
        this.setError(
          error.status === 422 && error.message
            ? error.message
            : 'Das hat gerade nicht geklappt. Bitte versuch es noch einmal.'
        );
      }
    }
  }

  /* ---------------- Rabattcode ---------------- */
  class EldrCartDiscount extends HTMLElement {
    constructor() {
      super();
      this.onSubmit = this.onSubmit.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onInput = this.onInput.bind(this);
    }

    connectedCallback() {
      this.form = this.querySelector('[data-eldr-discount-form]');
      this.input = this.querySelector('.eldr-discount__input');
      this.applyButton = this.querySelector('[data-eldr-discount-apply]');
      this.status = this.querySelector('[data-eldr-discount-status]');

      this.form?.addEventListener('submit', this.onSubmit);
      this.input?.addEventListener('input', this.onInput);
      this.addEventListener('click', this.onClick);

      /* Meldung aus dem letzten Vorgang übernehmen (Drawer wurde neu gerendert) */
      const flash = state.discountFlash;
      if (flash) {
        state.discountFlash = null;
        if (flash.value && this.input) this.input.value = flash.value;
        this.showStatus(flash.message, flash.type);
      }
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit);
      this.input?.removeEventListener('input', this.onInput);
      this.removeEventListener('click', this.onClick);
    }

    onInput() {
      if (this.input?.getAttribute('aria-invalid') === 'true') {
        this.input.removeAttribute('aria-invalid');
        this.showStatus('');
      }
    }

    onSubmit(event) {
      event.preventDefault();
      this.apply(this.input ? this.input.value : '');
    }

    onClick(event) {
      const removeButton = event.target.closest('[data-eldr-discount-remove]');
      if (removeButton) this.remove(removeButton.dataset.code || '');
    }

    showStatus(message, type = 'info') {
      if (!this.status) return;
      this.status.textContent = message || '';
      this.status.dataset.type = type;
      this.status.hidden = !message;
      if (this.input) {
        if (message && type === 'error') this.input.setAttribute('aria-invalid', 'true');
        else this.input.removeAttribute('aria-invalid');
      }
    }

    setBusy(busy) {
      this.toggleAttribute('data-busy', busy);
      if (this.applyButton) {
        this.applyButton.disabled = busy;
        if (busy) this.applyButton.setAttribute('aria-busy', 'true');
        else this.applyButton.removeAttribute('aria-busy');
      }
      this.querySelectorAll('[data-eldr-discount-remove]').forEach((button) => {
        button.disabled = busy;
      });
    }

    async activeCodes() {
      const cart = await request(cartJsUrl());
      return (cart?.discount_codes || []).filter((entry) => entry.applicable).map((entry) => entry.code);
    }

    async apply(rawCode) {
      const code = rawCode.trim();
      if (!code) {
        this.showStatus('Bitte gib einen Rabattcode ein.', 'error');
        this.input?.focus();
        return;
      }
      if (this.hasAttribute('data-busy')) return;

      this.setBusy(true);
      this.showStatus('');

      try {
        const existing = await this.activeCodes();
        if (existing.some((active) => sameCode(active, code))) {
          this.setBusy(false);
          this.showStatus(`Der Code „${code}“ ist bereits aktiv.`, 'info');
          return;
        }

        let data = await request(cartUpdateUrl(), {
          discount: [...existing, code].join(','),
          sections: SECTION_ID,
        });

        const codes = data?.discount_codes || [];
        const entry = codes.find((item) => sameCode(item.code, code));

        if (!entry || !entry.applicable) {
          /* Ungültige Codes nicht im Warenkorb liegen lassen */
          data = await request(cartUpdateUrl(), {
            discount: existing.join(','),
            sections: SECTION_ID,
          });
          state.discountFlash = {
            type: 'error',
            value: code,
            message: `Der Code „${code}“ ist ungültig oder gilt nicht für deinen Warenkorb.`,
          };
          renderDrawer(data);
          document.getElementById('EldrDiscountInput')?.focus({ preventScroll: true });
          return;
        }

        /* Neuer Code lässt sich evtl. nicht mit alten kombinieren */
        const dropped = existing.filter((old) => !codes.some((item) => sameCode(item.code, old) && item.applicable));
        if (dropped.length) {
          data = await request(cartUpdateUrl(), {
            discount: codes.filter((item) => item.applicable).map((item) => item.code).join(','),
            sections: SECTION_ID,
          });
        }

        state.discountFlash = {
          type: 'success',
          message: dropped.length
            ? `Code „${entry.code}“ angewendet. ${dropped.join(', ')} lässt sich damit nicht kombinieren und wurde entfernt.`
            : `Code „${entry.code}“ angewendet.`,
        };
        renderDrawer(data);
      } catch (error) {
        this.setBusy(false);
        this.showStatus('Der Code konnte gerade nicht geprüft werden. Bitte versuch es noch einmal.', 'error');
      }
    }

    async remove(code) {
      if (!code || this.hasAttribute('data-busy')) return;
      this.setBusy(true);
      try {
        const remaining = (await this.activeCodes()).filter((active) => !sameCode(active, code));
        const data = await request(cartUpdateUrl(), {
          discount: remaining.join(','),
          sections: SECTION_ID,
        });
        state.discountFlash = { type: 'info', message: `Code „${code}“ entfernt.` };
        renderDrawer(data);
      } catch (error) {
        this.setBusy(false);
        this.showStatus('Der Code konnte gerade nicht entfernt werden. Bitte versuch es noch einmal.', 'error');
      }
    }
  }

  /* ============================================================
     Doppelte "In den Warenkorb"-Anfragen zusammenfassen
     Das Theme bindet component-product-card.js pro Karte einmal ein
     (auf einer Collection-Seite dutzendfach). Dadurch haengen zwei
     Handler am selben Formular: EIN Klick schickt ZWEI identische
     Anfragen an /cart/add und legt zwei Stueck in den Warenkorb.
     Hier wird die zweite identische Anfrage innerhalb von
     ADD_DEDUPE_MS nicht erneut gesendet; beide Aufrufer bekommen
     dieselbe Antwort (Clone), damit das Rendern normal weiterlaeuft.
     ============================================================ */
  const ADD_DEDUPE_MS = 1000;
  const recentAdds = new Map();
  const nativeFetch = window.fetch.bind(window);

  function addFingerprint(body) {
    if (!body) return '';
    if (typeof body === 'string') return body;
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      const parts = [];
      body.forEach((value, key) => {
        parts.push(key + '=' + (typeof value === 'string' ? value : '[file]'));
      });
      return parts.sort().join('&');
    }
    return '';
  }

  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const method = String((init && init.method) || (typeof input !== 'string' && input && input.method) || 'GET').toUpperCase();

    if (method !== 'POST' || url.indexOf('/cart/add') === -1) {
      return nativeFetch(input, init);
    }

    const key = url + '|' + addFingerprint(init && init.body);
    const now = Date.now();
    const previous = recentAdds.get(key);

    if (previous && now - previous.at < ADD_DEDUPE_MS) {
      return previous.promise.then((response) => response.clone());
    }

    const promise = nativeFetch(input, init).then((response) => {
      recentAdds.set(key, { at: Date.now(), promise: Promise.resolve(response.clone()) });
      return response;
    });
    recentAdds.set(key, { at: now, promise });
    return promise;
  };

  /* ============================================================
     Sicherheitsnetz fuer "In den Warenkorb"
     Manche Karten-Skripte des Themes werden erst bei Bedarf nachgeladen.
     Wer sofort nach dem Seitenaufbau klickt, schickt das Formular dann
     klassisch ab und landet auf der Warenkorb-SEITE statt im Drawer.
     Hat das Theme das Submit schon selbst verhindert (defaultPrevented),
     bleibt dieser Handler stumm - sonst legt er selbst per AJAX an und
     oeffnet den Drawer. Laeuft in der Bubble-Phase, also NACH dem Theme.
     ============================================================ */
  async function handleStrayAddToCart(event) {
    if (event.defaultPrevented) return;
    const form = event.target;
    if (!form || form.tagName !== 'FORM') return;
    const action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;

    event.preventDefault();

    const body = new FormData(form);
    if (!body.has('sections')) body.append('sections', SECTION_ID);

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await response.json();
      if (!response.ok) {
        /* z. B. ausverkauft: Drawer trotzdem aktualisieren, Meldung kommt vom Theme */
        window.eventBus?.emit('render:cart:drawer');
        return;
      }
      renderDrawer(data);
      window.eventBus?.emit('open:cart:drawer', { scrollToTop: true });
    } catch (error) {
      /* Netz gerissen -> klassischer Weg, damit nichts verloren geht */
      form.submit();
    }
  }

  document.addEventListener('submit', handleStrayAddToCart);

  if (!customElements.get('eldr-cart-upsell')) customElements.define('eldr-cart-upsell', EldrCartUpsell);
  if (!customElements.get('eldr-cart-discount')) customElements.define('eldr-cart-discount', EldrCartDiscount);
})();
